import React, { useEffect, useState } from "react";
import { AnswersType, ParticipantExamQuestion } from "../../../types/question";
import { useTranslation } from "react-i18next";
import { TRANSLATION_CONFIG } from "../../../utils/consts/translation";
import { Header } from "./Summary/Header";
import { addLeadingZero } from "../../../utils/math";
import { ParticipantExam } from "../../../types/components";
import {
  CheckmarkFilled,
  CheckmarkOutline,
  FaceNeutral,
  Misuse,
  RadioButton,
} from "@carbon/icons-react";
import { Button } from "../../atom/Button";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../utils/consts/routes";

interface CompletedScreenProps {
  questions: ParticipantExamQuestion[];
  answers: AnswersType;
  examId?: string;
  resultExam: ParticipantExam | null;
  isPractice: boolean;
}

export const CompletedScreen = ({
  questions,
  answers,
  resultExam,
  isPractice,
}: CompletedScreenProps) => {
  const { t } = useTranslation(TRANSLATION_CONFIG.appNamespace);
  const navigate = useNavigate();
  const [points, setPoints] = useState<number>(0);
  const [percentage, setPercentage] = useState<number>(0);
  const [isPassed, setIsPassed] = useState<boolean>(false);

  useEffect(() => {
    localStorage.removeItem("terms");
    localStorage.removeItem("pesel");
  }, [questions, answers]);

  useEffect(() => {
    setPercentage(resultExam?.percentage || 0);
    setIsPassed(resultExam?.is_passed || false);
    setPoints(resultExam?.total_score || 0);
  }, [resultExam]);

  return (
    <>
      <Header title={"Results"} isResult isPractice={isPractice} />
      <div className={`pt-[80px]`}>
        {/* Top Section - Score */}
        <div className="flex flex-col items-center text-center space-y-3 pt-12">
          <div className={`rounded-full p-4 ${isPassed ? "bg-green-100" : "bg-red-100"}`}>
            {isPassed ? (
              <CheckmarkOutline className={"text-green-700"} size={42} />
            ) : (
              <FaceNeutral className={"text-red-500"} size={42} />
            )}
          </div>
          <div className="py-8">
            <p
              className={`text-[32px] font-bold leading-[46.4px] tracking-[0.002em] ${isPassed ? "text-green-700" : "text-red-500"}`}>
              {isPassed ? t("Congratulations!") : t("We’re sorry!")}
            </p>
            <p
              className={`text-[72px] font-bold leading-[72px] tracking-[0.002em] ${isPassed ? "text-green-700" : "text-red-500"}`}>
              {percentage}%
            </p>
          </div>
          <div
            className={`px-4 py-[10px] rounded-full font-satoshi text-[16px] font-[420] leading-[16px] tracking-[0.002em] ${isPassed ? "text-green-700 bg-green-100" : "text-red-500 bg-red-100"} `}>
            {points} / {questions.length}
          </div>
        </div>

        {/* Exam Overview */}
        <div className="flex justify-center mt-10 relative">
          <div className="bg-gray-100 w-full max-w-4xl mt-8 rounded-xl shadow-md p-6 relative">
            {/* Top-right corner icon */}
            <div className="absolute -top-6 -right-6">
              <div className="p-4 bg-white border rounded-full shadow-lg text-2xxl">
                {isPassed ? "👏" : "🙁"}
              </div>
            </div>
            <p className="text-[20px] font-medium leading-[25.2px] tracking-[0.1px] text-center text-black pt-1 pb-1.5">
              {t("Exam Overview")}
            </p>
            <div className="mt-4 space-y-6">
              {questions.map(({ id, exam_question }, index) => {
                const userAnswer = answers[id];
                const userDidNotAnswer = userAnswer === undefined || userAnswer === null;

                return (
                  <div
                    key={id}
                    className="border border-gray-200 rounded-lg p-6 shadow-sm bg-white">
                    {/* Question Header */}
                    <div className="flex items-start gap-3 mb-4 mt-4">
                      <p className="text-center text-[24px] font-light leading-[33.6px] tracking-[2px] p-4 pt-0 text-gray-376">
                        {addLeadingZero(index + 1)}
                      </p>
                      <p className="text-[18px] font-medium leading-[25.2px] tracking-[0.2px] text-gray-800 max-w-2xl">
                        {exam_question?.question_text}
                      </p>
                    </div>

                    {/* Answer Section */}
                    <div className="mt-8 space-y-3 ml-20 max-w-xl">
                      {exam_question?.exam_answers?.map(({ answer_text, id, is_correct }) => {
                        const isSelected = userAnswer === answer_text;
                        const isCorrectOption = is_correct;
                        const isWrongSelected = isSelected && !isCorrectOption;
                        const shouldMarkCorrectAsWrong = userDidNotAnswer && isCorrectOption;

                        return (
                          <div
                            key={id}
                            className={`flex items-center p-3 pt-0 rounded-lg transition ${
                              isCorrectOption
                                ? shouldMarkCorrectAsWrong
                                  ? "text-red-500"
                                  : "text-green-700"
                                : isWrongSelected
                                  ? "text-red-500"
                                  : "text-gray-700"
                            }`}>
                            {/* Custom Radio Button */}
                            <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                              {isCorrectOption ? (
                                shouldMarkCorrectAsWrong ? (
                                  <Misuse className="text-red-500" size={18} />
                                ) : isSelected ? (
                                  <CheckmarkFilled className="text-green-700" size={18} />
                                ) : (
                                  <CheckmarkOutline className="text-green-700" size={18} />
                                )
                              ) : isWrongSelected ? (
                                <Misuse className="text-red-500" size={18} />
                              ) : (
                                <RadioButton className="text-gray-400" size={19} />
                              )}
                            </div>

                            {/* Answer Text */}
                            <p className="ml-3 text-[16px] font-normal leading-[22.4px] tracking-[0.2px] text-left">
                              {answer_text}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex h-[132px] pt-16 pb-6 px-6">
              <Button
                type="primary"
                onClick={() => navigate(isPractice ? ROUTES.practice : ROUTES.dashboard)}
                label={t("Go Back")}
                additionalClassName="w-full"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
